import { isProjectIntegrationDefined } from "@services/integrations-service/autodesk/integrations-service-utils";
import { BaseService } from "@services/integrations-service/base-service";
import { INTEGRATION_ERROR_CODES } from "@services/integrations-service/integrations-service-constants";
import { isProcoreLegacyProjectIntegration } from "@services/integrations-service/integrations-type-guards";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";

/** Service that provides methods to manage the Procore 3rd party integration */
export class ProcoreService extends BaseService {
  integrationId = "procore";

  /**
   * @returns the IntegrationCompany entities of the user
   * @throws {Error} if it fails to get the entities
   */
  async getIntegrationCompanies() {
    const proxyRequest = this.coreApiClient.integration.getProcoreCompanies;
    const procoreCompanies = await this.request(proxyRequest);
    return procoreCompanies.map(procoreCompany => ({
      integrationId: "procore",
      id: procoreCompany.id.toString(),
      name: procoreCompany.name
    }));
  }

  /**
   * @returns the IntegrationProject entities for a given integration company
   * @throws {Error} if it fails to get the entities
   * @param companyId ID of the company
   */
  async getIntegrationProjects(companyId) {
    const proxyRequest = () => this.coreApiClient.integration.getProcoreProjects(companyId);
    const procoreProjects = await this.request(proxyRequest);
    const integrationProjects = procoreProjects.map(procoreProject => {
      const bcfProjectId = `${procoreProject.id}~${companyId}`;
      return {
        integrationId: "procore",
        id: procoreProject.id.toString(),
        name: procoreProject.name,
        companyId,
        rfisBcfProjectId: bcfProjectId,
        issuesOrObservationsBcfProjectId: bcfProjectId,
        type: "none"
      };
    });
    return integrationProjects;
  }

  /**
   * Handles errors originating from the CoreAPI proxy backend for requests issued to the Procore backend.
   * - If the error status is UNAUTHORIZED it attempts to update the token.
   * - If the error status is FORBIDDEN and for some specific error codes then it also attempts
   * to update the token.
   * @param error Error to handle
   * @throws {HttpUtils.FetchError} if it can't handle the error.
   */
  async handleIntegrationError(error) {
    switch (error.status) {
      case HttpUtils.HTTP_ERROR_CODE.Unauthorized:
        await this.handleTokenUpdate();
        break;
      case HttpUtils.HTTP_ERROR_CODE.Forbidden:
        if (
        // Check if the error body contains a raw Core API response error
        FaroCoreApiUtils.Client.isResponseError(error.errorBody) && (error.errorBody.error_v2 === INTEGRATION_ERROR_CODES.authentication_error || error.errorBody.error_v2 === INTEGRATION_ERROR_CODES.no_authorization_token)) {
          await this.handleTokenUpdate();
        } else {
          throw error;
        }
        break;
      default:
        throw error;
    }
  }

  /**
   * Updates the project connection data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param data Project integrations map
   */
  updateDataFormat(data) {
    const bcfProjectId = `${data.procore.projectId}~${data.procore.companyId}`;
    return Promise.resolve({
      ...data,
      ["procore.observations"]: {
        bcfProjectId
      },
      ["procore.rfis"]: {
        bcfProjectId
      }
    });
  }

  /**
   * @returns Whether there is a project connected in the legacy (HB) data format.
   * @param data Project integrations map
   */
  isConnectedInLegacyFormat(data) {
    return isProcoreLegacyProjectIntegration(data.procore);
  }

  /**
   * @returns Whether there is a project connected in the XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isConnectedInXGFormat(projectIntegrationsMap) {
    const isProcoreObservationsConnected = isProjectIntegrationDefined(projectIntegrationsMap["procore.observations"]);
    const isProcoreRfisConnected = isProjectIntegrationDefined(projectIntegrationsMap["procore.rfis"]);
    return isProcoreObservationsConnected && isProcoreRfisConnected;
  }

  /**
   * @returns Whether there is a project connected either in legacy or XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isConnected(projectIntegrationsMap) {
    return this.isConnectedInLegacyFormat(projectIntegrationsMap) || this.isConnectedInXGFormat(projectIntegrationsMap);
  }
}